import type mapboxgl from "mapbox-gl";
import {
  getAccessibilityPaletteValue,
  getColorForNDVIValue,
  getDefaultColorPaletteValue,
} from "../../shared/sl/soilAnalysisColors";
import { NDVI } from "../../shared/sl/soilColorUtil";

const normalizePaletteName = (
  paletteName?: string
): "default" | "accessibility" | "ndvi" => {
  const normalized = String(paletteName ?? "default")
    .trim()
    .toLowerCase();
  if (normalized === "accessibility") return "accessibility";
  if (normalized === "ndvi") return "ndvi";
  return "default";
};

// Helper: Create color stops for Mapbox step function
const createColorStops = (zoneData: any[]): any[] => {
  if (!zoneData || zoneData.length === 0) {
    return [0, "#ccc"];
  }
  const stops: any[] = [];
  const sortedZones = [...zoneData].sort((a, b) => a.min - b.min);
  sortedZones.forEach((zone, index) => {
    let color = zone.color;
    if (!color || typeof color !== "string" || !color.startsWith("#")) {
      color = "#ccc";
    }
    stops.push(zone.min, color);
  });
  const lastZone = sortedZones.at(-1);
  stops.push(lastZone.max, lastZone.color || "#ccc");
  return stops;
};

// Helper to get palette array
function getPaletteArray(paletteName: string, zoneCount: number): string[] {
  switch (normalizePaletteName(paletteName)) {
    case "accessibility":
      return Array.from({ length: zoneCount }, (_, i) =>
        getAccessibilityPaletteValue(i, zoneCount)
      );
    case "ndvi":
      return Array.from({ length: zoneCount }, (_, i) => {
        const ndviValue = zoneCount === 1 ? 0.5 : i / (zoneCount - 1);
        return getColorForNDVIValue(ndviValue);
      });
    case "default":
    default:
      return Array.from({ length: zoneCount }, (_, i) =>
        getDefaultColorPaletteValue(i, zoneCount)
      );
  }
}

// Main: Add soil image layer to map
export const addSoilImageLayer = (
  map: mapboxgl.Map,
  soilImageData: any,
  palette: string = "default",
  opacity: number = 60,
  sourceId: string = "soil-image-source",
  layerId: string = "soil-image-layer"
): boolean => {
  try {
    if (map?.getLayer(layerId)) map?.removeLayer(layerId);
    if (map?.getSource(sourceId)) map?.removeSource(sourceId);

    const effectivePalette = normalizePaletteName(
      soilImageData?.palette || palette
    );

    const paletteArray = getPaletteArray(
      effectivePalette,
      soilImageData?.zoneData?.length
    );

    const zoneDataWithColors = soilImageData?.zoneData?.map(
      (zone: any, index: number) => ({
        ...zone,
        color: paletteArray[index],
      })
    );
    const colorStops = createColorStops(zoneDataWithColors);

    let globalMin: number | null = null;
    let globalMax: number | null = null;
    if (effectivePalette === "ndvi") {
      const allMins = soilImageData?.zoneData.map((z: any) => z.min);
      const allMaxs = soilImageData?.zoneData.map((z: any) => z.max);
      globalMin = Math.min(...allMins);
      globalMax = Math.max(...allMaxs);
    }

    // Patch GeoJSON features to add "value" property based on "group"
    const patchedGeoJson = { ...soilImageData?.geoJsonData };
    if (patchedGeoJson && Array.isArray(patchedGeoJson?.features)) {
      patchedGeoJson.features = patchedGeoJson?.features.map((feature: any) => {
        let value;
        if (
          feature?.properties &&
          typeof feature?.properties?.group === "string"
        ) {
          const [minStr, maxStr] = feature?.properties?.group?.split("-") ?? [];
          const min = Number.parseFloat(minStr);
          const max = Number.parseFloat(maxStr);
          if (!Number.isNaN(min) && !Number.isNaN(max)) {
            value = (min + max) / 2;
            if (
              effectivePalette === "ndvi" &&
              globalMin !== null &&
              globalMax !== null &&
              globalMax !== globalMin
            ) {
              value = (value - globalMin) / (globalMax - globalMin);
            }
          }
        }
        return {
          ...feature,
          properties: {
            ...feature?.properties,
            value,
          },
        };
      });
    }
    const ndviStops = Object.entries(NDVI)
      .sort(([a], [b]) => Number(a) - Number(b))
      .flatMap(([threshold, color]) => [Number(threshold), color]);

    const fillColor: mapboxgl.Expression =
      effectivePalette === "ndvi"
        ? ["interpolate", ["linear"], ["get", "value"], ...ndviStops]
        : ["step", ["get", "value"], "#ccc", ...colorStops];

    // Add GeoJSON source with patched features
    map.addSource(sourceId, {
      type: "geojson",
      data: patchedGeoJson,
    });

    map.addLayer({
      id: layerId,
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": fillColor,
        "fill-opacity": opacity / 100,
        "fill-outline-color": "#fff",
      },
    });

    return true;
  } catch (error) {
    console.error("Error adding soil image layer:", error);
    return false;
  }
};

// Update soil layer colors dynamically
export const updateSoilLayerColors = (
  map: mapboxgl.Map,
  zoneData: any[],
  palette: string = "default",
  layerId: string = "soil-image-layer"
): void => {
  if (!map || !zoneData) return;
  try {
    if (map.getLayer(layerId)) {
      const effectivePalette = normalizePaletteName(palette);
      const paletteArray = getPaletteArray(effectivePalette, zoneData.length);
      const zoneDataWithColors = zoneData.map((zone, index) => ({
        ...zone,
        color: paletteArray[index],
      }));
      const colorStops = createColorStops(zoneDataWithColors);
      map.setPaintProperty(layerId, "fill-color", [
        "step",
        ["get", "value"],
        "#ccc",
        ...colorStops,
      ]);
    }
  } catch (error) {
    console.error("Error updating colors:", error);
  }
};

// Remove soil image layer and related sources
export const removeSoilImageLayer = (
  map: mapboxgl.Map,
  sourceId: string = "soil-image-source",
  layerId: string = "soil-image-layer"
): void => {
  if (!map) return;
  try {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);
    const geojsonSourceId = `${sourceId}-geojson`;
    const geojsonLayerId = `${layerId}-geojson`;
    const geojsonOutlineLayerId = `${layerId}-geojson-outline`;
    if (map.getLayer(geojsonLayerId)) map.removeLayer(geojsonLayerId);
    if (map.getLayer(geojsonOutlineLayerId))
      map.removeLayer(geojsonOutlineLayerId);
    if (map.getSource(geojsonSourceId)) map.removeSource(geojsonSourceId);
  } catch (error) {
    console.error("Error removing soil layers:", error);
  }
};

// Update soil layer opacity
export const updateSoilLayerOpacity = (
  map: mapboxgl.Map,
  opacity: number,
  layerId: string = "soil-image-layer-geojson"
): void => {
  if (!map || opacity < 0 || opacity > 1) return;
  try {
    if (map.getLayer(layerId)) {
      map.setPaintProperty(layerId, "fill-opacity", opacity);
    }
  } catch (error) {
    console.error("Error updating soil layer opacity:", error);
  }
};

// Validate GeoJSON data
export const validateGeoJSON = (data: any): boolean => {
  if (!data || typeof data !== "object") {
    console.error("GeoJson data is not valid.");
    return false;
  }
  if (data.type === "FeatureCollection") {
    if (!Array.isArray(data.features)) {
      console.error("GeoJSON FeatureCollection must have a features array.");
      return false;
    }
    return data.features.every((feature: any) => {
      if (feature?.type !== "Feature") {
        console.error("All features must be GeoJSON Feature objects.");
        return false;
      }
      if (!feature.geometry?.type || !feature.geometry?.coordinates) {
        console.error("All features must have valid geometry.");
        return false;
      }
      return true;
    });
  }
  console.error(
    "Unsupported GeoJSON type. Only FeatureCollection is supported."
  );
  return false;
};
